/**
 * Purchasing authority — what this account can sign without going to DGS.
 *
 * Two surfaces, one payload (`workspace.purchasing_authority`):
 *   <PurchasingAuthorityChip>  the always-visible tier in the topbar stats
 *   <PurchasingAuthorityCard>  the sidebar card with the ceiling and vehicles
 *
 * The design rule here is that a tier badge on its own is dangerous. "Tier 2"
 * tells a rep nothing and quietly implies the account signs its own paper —
 * which is false for the entities DGS cross-references to a parent. So the
 * ceiling always travels with the tier, and `via_parent` names the parent on
 * both surfaces. When there is no tier at all the card says why and who to
 * ask, rather than rendering an empty field that reads like missing data.
 */

import React, { useState } from 'react';
import { Gavel, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

/** Ceilings are per-transaction dollars, `'unlimited'`, or null (N/A). */
export function fmtCeiling(v) {
  if (v === 'unlimited') return 'No cap';
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1e6) {
    const m = n / 1e6;
    // $249,999.99 must not round to "$0.2M"; only whole-ish millions compress.
    return `$${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${n.toLocaleString()}`;
}

/** Vehicles a security seller actually reaches for, in the order they matter. */
const HEADLINE_METHODS = [
  ['software_licensing_program', 'Software Licensing Program'],
  ['master_agreements', 'Master agreement'],
  ['cmas', 'CMAS'],
  ['open_market_competitive', 'Open market'],
  ['ncb_contract', 'Sole source (NCB)'],
];

function tierToneClass(pa) {
  if (!pa || !pa.tier) return 'aw-pa-tone-none';
  return `aw-pa-tone-${pa.tier}`;
}

/** Compact, always-visible. Tier plus the ceiling that gives it meaning. */
export function PurchasingAuthorityChip({ purchasingAuthority: pa }) {
  if (!pa) return null;

  if (!pa.tier) {
    return (
      <span className="aw-mini-stat aw-pa-chip aw-pa-tone-none" title={pa.detail || pa.summary}>
        <strong>No DGS tier</strong> authority
      </span>
    );
  }

  const viaParent = pa.status === 'via_parent';
  const title = viaParent
    ? `${pa.summary} Signs up to ${fmtCeiling(pa.itCeiling)} per IT transaction under that authority.`
    : `${pa.headline} Up to ${fmtCeiling(pa.itCeiling)} per IT transaction without DGS.`;

  return (
    <span className={`aw-mini-stat aw-pa-chip ${tierToneClass(pa)}`} title={title}>
      <strong>Tier {pa.tier}</strong>
      {viaParent && <span className="aw-pa-via">via parent</span>}
      <span className="aw-pa-chip-ceiling">{fmtCeiling(pa.itCeiling)} IT</span>
    </span>
  );
}

function Rows({ pa }) {
  const rows = HEADLINE_METHODS
    .map(([key, label]) => [label, pa.thresholds?.[key]?.it])
    .filter(([, v]) => v !== undefined && v !== null);

  if (rows.length === 0) return null;
  return (
    <dl className="aw-pa-rows">
      {rows.map(([label, v]) => (
        <div className="aw-pa-row" key={label}>
          <dt>{label}</dt>
          <dd>{fmtCeiling(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The sidebar card. Collapsed it is one sentence and the headline ceiling;
 * expanded it lists the per-vehicle IT ceilings and cites the DGS source.
 */
export function PurchasingAuthorityCard({ purchasingAuthority: pa }) {
  const [open, setOpen] = useState(false);

  if (!pa) return null;

  const hasTier = Boolean(pa.tier);
  // Kept short: a long badge wraps the header onto two lines, and the card's
  // own body already carries the full explanation.
  const heading = hasTier
    ? `Tier ${pa.tier}${pa.status === 'verified' ? ' · confirmed' : ''}`
    : 'Not listed';

  return (
    <section className={`aw-pa-card ${tierToneClass(pa)}`}>
      <header className="aw-pa-head">
        <Gavel size={15} />
        <span className="aw-pa-title">Purchasing authority</span>
        <span className="aw-pa-badge">{heading}</span>
      </header>

      <p className="aw-pa-summary">
        {hasTier ? (pa.status === 'delegated' ? pa.headline : pa.summary) : pa.summary}
      </p>

      {pa.status === 'via_parent' && (
        <p className="aw-pa-note">
          Its own procurement staff do not hold the delegation — {pa.authorityOf} signs.
        </p>
      )}
      {pa.scopeNote && <p className="aw-pa-note">DGS notes: {pa.scopeNote}.</p>}
      {!hasTier && pa.detail && <p className="aw-pa-note">{pa.detail}</p>}

      {hasTier && (
        <>
          <div className="aw-pa-headline">
            <span className="aw-pa-headline-value">{fmtCeiling(pa.itCeiling)}</span>
            <span className="aw-pa-headline-label">
              largest IT buy signed without DGS
            </span>
          </div>

          <button
            className="aw-pa-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            Ceiling by vehicle
          </button>

          {open && (
            <>
              <Rows pa={pa} />
              <p className="aw-pa-fineprint">
                Per-transaction IT ceilings. Above them the buy goes to DGS.
                {pa.listedName ? ` DGS lists this account as “${pa.listedName}”.` : ''}
              </p>
            </>
          )}
        </>
      )}

      <footer className="aw-pa-source">
        {pa.paalUrl ? (
          <a href={pa.paalUrl} target="_blank" rel="noopener noreferrer">
            DGS Tier {pa.tier} approval letter <ExternalLink size={11} />
          </a>
        ) : (
          <a href={pa.source?.listingUrl} target="_blank" rel="noopener noreferrer">
            DGS purchasing authority list <ExternalLink size={11} />
          </a>
        )}
        {pa.source?.asOf && <span className="aw-pa-asof">checked {pa.source.asOf}</span>}
      </footer>
    </section>
  );
}

export default PurchasingAuthorityCard;
